import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useForm} from "react-hook-form";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {z} from "zod";

import {Button} from "@/components/ui/button";
import {Form, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {PublicPageShell} from "@/src/modules/core/components";
import {AppVersionInfo} from "@/src/modules/core/components/app-version-info";
import {LOGO_URL_DESKTOP} from "@/src/modules/core/utils/constants";
import {peekRequiresProfileCompletionPending} from "@/src/modules/resident-claim/lib/claim-profile-pending";

import {PasswordInput, SessionEndedBanner} from "../components";import {useLogin} from "../hooks";
import {clearSessionEndedLoginSearch} from "../lib/session-ended-copy";
import {loginSchema} from "../schemas";
import {authSerializer} from "../services/serializer";

export default function LoginPage() {
  const {mutate: login, isPending} = useLogin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const afterClaimVerify = peekRequiresProfileCompletionPending();

  const form = useForm<z.infer<typeof loginSchema>>({
    defaultValues: {
      user_name: "",
      password: "",
      remember_me: true,
    },
    resolver: zodResolver(loginSchema),
  });

  const handleFormSubmit = (data: z.infer<typeof loginSchema>) => {
    clearSessionEndedLoginSearch(navigate, searchParams);
    const loginData = authSerializer.serializeLogin(data);

    login(loginData);
  };

  return (
    <PublicPageShell>
      <div className="rounded-lg bg-background p-6 shadow-lg sm:p-8">
          <div className="mb-8 flex flex-col items-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full">
              <img src={LOGO_URL_DESKTOP} alt="Image" height="100" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">STARSPRO</h1>
            <p className="text-muted-foreground">Security Management System</p>
          </div>

          <h2 className="mb-6 text-center text-xl font-semibold text-foreground">
            Sign in to your account
          </h2>

          <SessionEndedBanner />

          {afterClaimVerify ? (
            <p className="mb-4 text-center text-sm text-muted-foreground">
              Sign in with the email and password you chose when claiming your account. You will
              complete your profile next.
            </p>
          ) : null}

          <Form {...form}>
            <form
              id="login-form"
              className="flex flex-col gap-4"
              onSubmit={form.handleSubmit(handleFormSubmit)}
            >
              <FormField
                control={form.control}
                name="user_name"
                render={({field, formState}) => (
                  <FormItem>
                    <FormLabel>{afterClaimVerify ? "Email" : "Username"}</FormLabel>
                    <Input
                      id="user_name"
                      type={afterClaimVerify ? "email" : "text"}
                      placeholder={
                        afterClaimVerify ? "you@example.com" : "Enter your username"
                      }
                      className="border-border"
                      disabled={isPending}
                      {...field}
                    />
                    {formState.errors.user_name && (
                      <FormMessage>{formState.errors.user_name.message}</FormMessage>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({field, formState}) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>Password</FormLabel>
                      <Link
                        to="/forgot"
                        className="text-sm font-medium text-primary hover:text-primary/80"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <PasswordInput {...field} />
                    {formState.errors.password && (
                      <FormMessage>{formState.errors.password.message}</FormMessage>
                    )}
                  </FormItem>
                )}
              />

              {/* <FormField
                control={form.control}
                name="remember_me"
                render={({field}) => (
                  <FormItem>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember_me"
                        disabled={isPending}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                      <Label
                        htmlFor="remember_me"
                        className="text-sm font-medium leading-none text-muted-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Remember me
                      </Label>
                    </div>
                  </FormItem>
                )}
              /> */}

              <Button type="submit" className="w-full" disabled={isPending}>
                {isPending ? (
                  <>
                    Signing in...
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>
          </Form>
        </div>

      <div className="mt-6 flex flex-col items-center gap-2">
        <AppVersionInfo />
        <p className="text-center text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} STARSPRO. All rights reserved.
        </p>
      </div>
    </PublicPageShell>
  );
}
